import { ref } from 'vue'
import { defineStore } from 'pinia'
import { http } from '@/helpers/http'
import { topBarStateKey } from '@/helpers/top-bars'
import type { TTopBar, TTopBarUserState, TTopBarVariant } from '@/types/top-bars'

export const useTopBarsStore = defineStore('top-bars', () => {
  const featureBar = ref<TTopBar | null>(null)
  const promoBar = ref<TTopBar | null>(null)
  const state = ref<Record<string, TTopBarUserState>>({})
  const bars = ref<TTopBar[]>([])

  let activeBarsPromise: Promise<void> | null = null

  async function fetchActiveBars(): Promise<void> {
    activeBarsPromise ??= loadActiveBars()

    return activeBarsPromise
  }

  async function loadActiveBars(): Promise<void> {
    const [activeBars, states] = await Promise.all([
      http.get<TTopBar[]>('/top_bars/active'),
      http.get<TTopBarUserState[]>('/top_bar_states'),
    ])

    state.value = Object.fromEntries((states ?? []).map((entry) => [entry.bar_key, entry]))
    promoBar.value = activeBars?.find((bar) => bar.type === 'promo') ?? null
    featureBar.value = activeBars?.find((bar) => bar.type === 'feature') ?? null
  }

  function canShow(variant: TTopBarVariant, id: number | string, maxViews: number | null = null): boolean {
    const key = topBarStateKey(variant, id)
    const entry = state.value[key]

    if (entry?.dismissed) {
      return false
    }
    if (sessionStorage.getItem(sessionViewKey(key))) {
      return true
    }

    return maxViews === null || (entry?.views ?? 0) < maxViews
  }

  async function registerView(variant: TTopBarVariant, id: number | string): Promise<void> {
    const key = topBarStateKey(variant, id)
    const sessionKey = sessionViewKey(key)

    if (sessionStorage.getItem(sessionKey)) {
      return
    }

    sessionStorage.setItem(sessionKey, '1')
    const updated = await http.post<TTopBarUserState>('/top_bar_states/view', { bar_key: key })
    state.value = { ...state.value, [key]: updated }
  }

  async function dismiss(variant: TTopBarVariant, id: number | string): Promise<void> {
    const key = topBarStateKey(variant, id)
    const entry = state.value[key] ?? { bar_key: key, views: 0, dismissed: false }
    state.value = { ...state.value, [key]: { ...entry, dismissed: true } }

    await http.post('/top_bar_states/dismiss', { bar_key: key })
  }

  function sessionViewKey(key: string): string {
    return `top-bar-viewed-${key}`
  }

  async function fetchBars(): Promise<void> {
    const res = await http.get<TTopBar[]>('/top_bars')
    bars.value = res ?? []
  }

  async function createBar(bar: Omit<TTopBar, 'id'>): Promise<TTopBar> {
    return http.post<TTopBar>('/top_bars', bar)
  }

  async function updateBar(bar: TTopBar): Promise<TTopBar> {
    return http.post<TTopBar>(`/top_bars/${bar.id}`, bar)
  }

  async function deleteBar(id: number): Promise<void> {
    await http.delete(`/top_bars/${id}`)
  }

  // test promo bar
  // promoBar.value = {
  //   id: 2,
  //   type: 'promo',
  //   message: 'Save 20% on PRO and Premium',
  //   badge: '20% OFF',
  //   link_label: 'Upgrade',
  //   link_url: '/account',
  //   starts_on: '2026-09-01',
  //   ends_on: '2026-09-30',
  //   max_views: null,
  //   active: true,
  // }

  // test feature bar
  // featureBar.value = {
  //   id: 1,
  //   type: 'feature',
  //   message: 'Presentations are here - we added 5 generations so you can try them',
  //   badge: 'NEW',
  //   link_label: 'Try presentations',
  //   link_url: '/creator/wordgenie/presentation',
  //   starts_on: '2026-09-01',
  //   ends_on: '2026-09-30',
  //   max_views: null,
  //   active: true,
  // }

  return {
    featureBar,
    promoBar,
    bars,
    fetchActiveBars,
    canShow,
    registerView,
    dismiss,
    fetchBars,
    createBar,
    updateBar,
    deleteBar,
  }
})
